import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Input from '../components/Input';
import { PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import IconButton from '../components/shared/IconButton';
import { ActionButtons, ActionContainer } from '../components/shared/ActionButtons';
import RoomSetupModal from '../components/RoomSetupModal';
import {
  SearchResults,
  SearchResultsSection,
  SearchResultsHeader,
  SearchResultItem,
  ItemInfo,
  LocationInfo
} from '../components/shared/SearchResults';
import Tooltip from '../components/shared/Tooltip';
import { TooltipContainer } from '../components/shared/Tooltip';
import UsageLimitModal from '../components/UsageLimitModal';
import SubscriptionSuccessModal from '../components/SubscriptionSuccessModal';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const DashboardSearchBar = styled(Input)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  width: 100%;
`;

const SearchContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const RoomCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const RoomTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.25rem;
`;

const RoomStats = styled.div`
  margin: ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const StatBox = styled.div`
  background: ${({ theme }) => `${theme.colors.primary}10`};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  span {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const RoomActions = ActionContainer;

const EditableText = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  input {
    margin: 0;
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const RoomHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ItemCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid #eee;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  /* Ensure tooltips appear above the scrollable container */
  ${TooltipContainer} {
    z-index: 10;
  }
`;

const Dashboard = () => {
  const [rooms, setRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ bins: [], items: [] });
  const [loading, setLoading] = useState(false);
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  const [editingRoom, setEditingRoom] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [isRoomSetupOpen, setIsRoomSetupOpen] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setShowSuccessModal(params.get('success') === 'true');
  }, [location]);

  const fetchRooms = async () => {
    try {
      // First get all rooms
      const roomsRef = collection(db, 'rooms');
      const q = query(roomsRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      // Get rooms and calculate actual item counts
      const roomsPromises = querySnapshot.docs.map(async (roomDoc) => {
        const roomData = { id: roomDoc.id, ...roomDoc.data() };
        
        // Count items for this room
        const itemsRef = collection(db, 'items');
        const itemsQuery = query(itemsRef, where('roomId', '==', roomDoc.id));
        const itemsSnapshot = await getDocs(itemsQuery);
        
        // Update room document with actual count if it differs
        const actualCount = itemsSnapshot.docs.length;
        if (actualCount !== roomData.itemCount) {
          const roomRef = doc(db, 'rooms', roomDoc.id);
          await updateDoc(roomRef, { itemCount: actualCount });
          roomData.itemCount = actualCount;
        }

        return roomData;
      });

      const roomsData = await Promise.all(roomsPromises);
      setRooms(roomsData);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [user]);

  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults({ bins: [], items: [] });
      return;
    }

    setLoading(true);
    try {
      const itemsRef = collection(db, 'items');
      const searchValue = value.toLowerCase();
      
      const roomsRef = collection(db, 'rooms');
      const roomsQuery = query(roomsRef, where('userId', '==', user.uid));
      const roomsSnapshot = await getDocs(roomsQuery);
      const roomIds = roomsSnapshot.docs.map(doc => doc.id);

      // Get all bins for the user's rooms
      const binsRef = collection(db, 'bins');
      const binsQuery = query(binsRef, where('roomId', 'in', roomIds));
      const binsSnapshot = await getDocs(binsQuery);
      
      // Find matching bins
      const matchingBins = await Promise.all(
        binsSnapshot.docs
          .filter(binDoc => {
            const binData = binDoc.data();
            const binName = binData?.name || '';
            const binNumber = binData?.number || '';
            return binName.toLowerCase().includes(searchValue) || 
                   binNumber.toString().includes(searchValue);
          })
          .map(async (binDoc) => {
            const binData = { id: binDoc.id, ...binDoc.data() };
            try {
              const roomRef = doc(db, 'rooms', binData.roomId);
              const roomDoc = await getDoc(roomRef);
              if (!roomDoc.exists()) return null;
              
              return {
                ...binData,
                room: { id: roomDoc.id, ...roomDoc.data() }
              };
            } catch (error) {
              console.error('Error processing bin:', error);
              return null;
            }
          })
      );

      // Get items by name match
      const itemsQuery = query(itemsRef, where('roomId', 'in', roomIds));
      const itemsSnapshot = await getDocs(itemsQuery);
      const matchingItems = itemsSnapshot.docs.filter(doc => {
        const itemData = doc.data();
        const itemName = itemData?.name || '';
        return itemName.toLowerCase().includes(searchValue);
      });

      const processedItems = await Promise.all(
        matchingItems.map(async (docSnapshot) => {
          const itemData = { id: docSnapshot.id, ...docSnapshot.data() };
          
          try {
            const roomRef = doc(db, 'rooms', itemData.roomId);
            const roomDoc = await getDoc(roomRef);
            
            if (!roomDoc.exists()) {
              return null;
            }
            const roomData = { id: roomDoc.id, ...roomDoc.data() };
            
            const binRef = doc(db, 'bins', itemData.binId);
            const binDoc = await getDoc(binRef);
            
            if (!binDoc.exists()) {
              return null;
            }
            const binData = { id: binDoc.id, ...binDoc.data() };

            // Only check for item name, allow unnamed bins
            if (!itemData.name || !roomData.name) {
              return null;
            }

            return {
              ...itemData,
              room: roomData,
              bin: binData
            };
          } catch (error) {
            console.error('Error processing item:', error);
            return null;
          }
        })
      );

      setSearchResults({
        bins: matchingBins.filter(bin => bin !== null),
        items: processedItems.filter(item => item !== null)
      });
      
    } catch (error) {
      console.error('Error searching items:', error);
    }
    setLoading(false);
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room.id);
    setEditValue(room.name);
  };

  const handleSaveRoom = async (roomId) => {
    try {
      setError('');
      const room = rooms.find(r => r.id === roomId);
      
      // Check for duplicate names
      if (editValue !== room.name) {
        const roomsRef = collection(db, 'rooms');
        const q = query(
          roomsRef, 
          where('userId', '==', user.uid),
          where('name', '==', editValue)
        );
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setError('A room with this name already exists');
          return;
        }
      }

      const roomRef = doc(db, 'rooms', roomId);
      await updateDoc(roomRef, { name: editValue });
      setRooms(rooms.map(r => 
        r.id === roomId ? { ...r, name: editValue } : r
      ));
      setEditingRoom(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating room:', error);
      setError('Failed to update room name');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room and all its contents?')) {
      return;
    }

    try {
      // Delete all items in the room
      const itemsRef = collection(db, 'items');
      const itemsQuery = query(itemsRef, where('roomId', '==', roomId));
      const itemsSnapshot = await getDocs(itemsQuery);
      
      await Promise.all(itemsSnapshot.docs.map(async (doc) => {
        const item = doc.data();
        if (item.photoUrl) {
          const photoRef = ref(storage, item.photoUrl);
          await deleteObject(photoRef);
        }
        await deleteDoc(doc.ref);
      }));

      // Delete all bins in the room
      const binsRef = collection(db, 'bins');
      const binsQuery = query(binsRef, where('roomId', '==', roomId));
      const binsSnapshot = await getDocs(binsQuery);
      
      await Promise.all(binsSnapshot.docs.map(doc => deleteDoc(doc.ref)));

      // Delete the room
      await deleteDoc(doc(db, 'rooms', roomId));
      setRooms(rooms.filter(r => r.id !== roomId));
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  const handleAddRoom = async () => {
    if (!isPremium) {
      const roomsQuery = query(
        collection(db, 'rooms'),
        where('userId', '==', user.uid)
      );
      const roomsSnapshot = await getDocs(roomsQuery);
      
      if (roomsSnapshot.docs.length >= 2) {
        setShowLimitModal(true);
        return;
      }
    }
    setIsRoomSetupOpen(true);
  };

  return (
    <Container>
      <SubscriptionSuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
      <Header>
        <h1>My Rooms</h1>
        <Button onClick={handleAddRoom}>Add Room</Button>
      </Header>

      <SearchContainer>
        <DashboardSearchBar
          type="text"
          placeholder="Search items or bins..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
        {(searchResults.bins.length > 0 || searchResults.items.length > 0) && (
          <SearchResults>
            {searchResults.bins.length > 0 && (
              <SearchResultsSection>
                <SearchResultsHeader>Bins ({searchResults.bins.length})</SearchResultsHeader>
                {searchResults.bins.map(bin => (
                  <SearchResultItem key={bin.id}>
                    <ItemInfo>
                      <h4>Bin {bin.number}{bin.name ? ` (${bin.name})` : ''}</h4>
                      <LocationInfo>
                        Located in {bin.room.name}
                      </LocationInfo>
                    </ItemInfo>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/inventory/${bin.room.id}`)}
                    >
                      View
                    </Button>
                  </SearchResultItem>
                ))}
              </SearchResultsSection>
            )}

            {searchResults.items.length > 0 && (
              <SearchResultsSection>
                <SearchResultsHeader>Items ({searchResults.items.length})</SearchResultsHeader>
                {searchResults.items.map(item => (
                  <SearchResultItem key={item.id}>
                    <ItemInfo>
                      <h4>{item.name}</h4>
                      <LocationInfo>
                        Found in {item.room.name} → Bin {item.bin.number}
                        {item.bin.name ? ` (${item.bin.name})` : ''}
                      </LocationInfo>
                    </ItemInfo>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/inventory/${item.room.id}`)}
                    >
                      View
                    </Button>
                  </SearchResultItem>
                ))}
              </SearchResultsSection>
            )}
          </SearchResults>
        )}
      </SearchContainer>

      <RoomGrid>
        {rooms.map(room => (
          <RoomCard
            key={room.id}
            onClick={() => !editingRoom && navigate(`/inventory/${room.id}`)}
          >
            <RoomHeader>
              {editingRoom === room.id ? (
                <EditableText>
                  <Input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ActionButtons>
                    <Tooltip text="Save Room Name">
                      <IconButton onClick={(e) => {
                        e.stopPropagation();
                        handleSaveRoom(room.id);
                      }}>
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text="Cancel Edit">
                      <IconButton onClick={(e) => {
                        e.stopPropagation();
                        setEditingRoom(null);
                      }}>
                        <XMarkIcon />
                      </IconButton>
                    </Tooltip>
                  </ActionButtons>
                </EditableText>
              ) : (
                <RoomTitle>{room.name}</RoomTitle>
              )}
            </RoomHeader>

            <RoomStats>
              <StatBox>
                <span>{room.itemCount || 0}</span>
                <p>Items</p>
              </StatBox>
              {room.binCount && (
                <StatBox>
                  <span>{room.binCount}</span>
                  <p>Bins</p>
                </StatBox>
              )}
            </RoomStats>

            <RoomActions>
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/inventory/${room.id}`);
                }}
              >
                View Room
              </Button>
              <ActionButtons>
                <Tooltip text="Edit Room Name">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditRoom(room);
                    }}
                  >
                    <PencilIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip text="Delete Room">
                  <IconButton
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRoom(room.id);
                    }}
                  >
                    <TrashIcon />
                  </IconButton>
                </Tooltip>
              </ActionButtons>
            </RoomActions>
          </RoomCard>
        ))}
      </RoomGrid>

      <RoomSetupModal
        isOpen={isRoomSetupOpen}
        onClose={() => setIsRoomSetupOpen(false)}
        onRoomCreated={() => {
          setIsRoomSetupOpen(false);
          fetchRooms();
        }}
      />

      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        type="rooms"
      />
    </Container>
  );
};

export default Dashboard; 